import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import ImageUploader from "../components/ImageUploader";

const DEFAULT_FORM = {
  title: "",
  excerpt: "",
  content: "",
  category: "Brewing & The Art of Tasting",
  tags: "",
  cover_image:
    "https://images.unsplash.com/photo-1753009712810-3f72c3f72548?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjB0ZWElMjBwcmVwYXJhdGlvbnxlbnwwfHx8fDE3Nzc1NDY2MTN8MA&ixlib=rb-4.1.0&q=85",
  author_name: "The Tea List Editors",
  author_avatar: "",
  read_time: 5,
  featured: false,
  status: "published",
  publish_date: "",
  sources: "",
};

const sourcesToText = (list) =>
  (list || []).map((s) => [s.title, s.publisher, s.url].filter(Boolean).join(" | ")).join("\n");

const textToSources = (text) =>
  text
    .split("\n")
    .map((line) => line.split("|").map((x) => x.trim()))
    .filter((parts) => parts[0])
    .map(([title, publisher = "", url = ""]) => ({ title, publisher, url }));

const CATEGORIES = [
  "Origins & History",
  "Types of Tea",
  "Global Tea Regions",
  "Brewing & The Art of Tasting",
  "Curious Tea Stories",
  "Health, Wellness & Food Pairings",
];

// Convert an ISO date string to the YYYY-MM-DDTHH:mm format that
// <input type="datetime-local"> expects (no seconds, no timezone).
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function PostEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    http
      .get(`/admin/posts/${id}`)
      .then(({ data }) => {
        setForm({
          ...DEFAULT_FORM,
          ...data,
          tags: (data.tags || []).join(", "),
          sources: sourcesToText(data.sources),
          status: data.status || "published",
          publish_date: toLocalInput(data.created_at),
        });
      })
      .catch(() => {
        toast.error("Post not found.");
        navigate("/admin");
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { publish_date, ...rest } = form;
    const payload = {
      ...rest,
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      read_time: Number(form.read_time) || 5,
      sources: textToSources(form.sources),
    };
    // When publishing, send an explicit created_at:
    //   - user picked a date  -> use it
    //   - user left it empty  -> use "now" so the post surfaces at the top
    if (payload.status === "published") {
      payload.created_at = publish_date
        ? new Date(publish_date).toISOString()
        : new Date().toISOString();
    }
    try {
      if (isEdit) {
        await http.put(`/posts/${id}`, payload);
        toast.success(payload.status === "published" ? "Post published." : "Post saved.");
      } else {
        await http.post("/posts", payload);
        toast.success(payload.status === "published" ? "Post published." : "Draft saved.");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
        <p className="font-mono text-xs uppercase tracking-widest text-[#66726d] animate-pulse">
          loading…
        </p>
      </div>
    );
  }

  let buttonLabel;
  if (saving) buttonLabel = "Saving…";
  else if (form.status === "draft") buttonLabel = isEdit ? "Save draft" : "Save as draft";
  else buttonLabel = isEdit ? "Update & publish" : "Publish";

  return (
    <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-16" data-testid="post-editor-page">
      <p className="overline mb-3">{isEdit ? "Edit post" : "New post"}</p>
      <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
        {isEdit ? "Refine this piece." : "Draft something new."}
      </h1>

      <form onSubmit={submit} className="mt-10 space-y-8" data-testid="post-editor-form">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">Title</label>
          <input
            type="text"
            required
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            className="ghost-input mt-2"
            data-testid="editor-title-input"
          />
        </div>

        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">Excerpt</label>
          <textarea
            required
            rows={2}
            value={form.excerpt}
            onChange={(e) => set("excerpt", e.target.value)}
            className="w-full bg-transparent border-b border-[#151e1a] focus:border-[#1e4a35] outline-none py-3 resize-none mt-2"
            data-testid="editor-excerpt-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">Category</label>
            <select
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className="ghost-input mt-2 bg-[#f8f4ec]"
              data-testid="editor-category-select"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">Tags (comma-separated)</label>
            <input
              type="text"
              value={form.tags}
              onChange={(e) => set("tags", e.target.value)}
              placeholder="matcha, brewing, japan"
              className="ghost-input mt-2"
              data-testid="editor-tags-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">Cover image</label>
            <input
              type="url"
              required
              value={form.cover_image}
              onChange={(e) => set("cover_image", e.target.value)}
              placeholder="Paste a URL, or upload below"
              className="ghost-input mt-2"
              data-testid="editor-cover-input"
            />
            <ImageUploader
              folder="posts/covers"
              label="Upload cover"
              onUploaded={(url) => set("cover_image", url)}
            />
            {form.cover_image ? (
              <img
                src={form.cover_image}
                alt="Cover preview"
                className="mt-3 max-h-32 object-cover border border-[#e2d9c6]"
                data-testid="editor-cover-preview"
              />
            ) : null}
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">Read time (min)</label>
            <input
              type="number"
              min={1}
              max={60}
              value={form.read_time}
              onChange={(e) => set("read_time", e.target.value)}
              className="ghost-input mt-2"
              data-testid="editor-readtime-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">Author name</label>
            <input
              type="text"
              value={form.author_name}
              onChange={(e) => set("author_name", e.target.value)}
              className="ghost-input mt-2"
              data-testid="editor-author-input"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">Author avatar (optional)</label>
            <input
              type="url"
              value={form.author_avatar}
              onChange={(e) => set("author_avatar", e.target.value)}
              placeholder="Paste a URL, or upload below"
              className="ghost-input mt-2"
              data-testid="editor-avatar-input"
            />
            <ImageUploader
              folder="authors/avatars"
              label="Upload avatar"
              onUploaded={(url) => set("author_avatar", url)}
            />
          </div>
        </div>

        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">Content</label>
          <textarea
            required
            rows={16}
            value={form.content}
            onChange={(e) => set("content", e.target.value)}
            className="w-full bg-white border border-[#e2d9c6] focus:border-[#1e4a35] outline-none p-4 resize-y mt-2 font-light leading-relaxed"
            placeholder="Write freely. Use blank lines between paragraphs."
            data-testid="editor-content-input"
          />
        </div>

        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
            Sources &amp; further reading
            <span className="normal-case tracking-normal text-[#66726d]/80"> — one per line: Title | Publisher | URL</span>
          </label>
          <textarea
            rows={5}
            value={form.sources}
            onChange={(e) => set("sources", e.target.value)}
            className="w-full bg-white border border-[#e2d9c6] focus:border-[#1e4a35] outline-none p-4 resize-y mt-2 font-mono text-xs leading-relaxed"
            placeholder="Tea: History, Terroirs, Varieties | Firefly Books | https://..."
            data-testid="editor-sources-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">Status</label>
            <select
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
              className="ghost-input mt-2 bg-[#f8f4ec]"
              data-testid="editor-status-select"
            >
              <option value="draft">Draft (hidden)</option>
              <option value="published">Published (live)</option>
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
              Publish date {form.status === "published" ? "" : "(used when you publish)"}
            </label>
            <input
              type="datetime-local"
              value={form.publish_date}
              onChange={(e) => set("publish_date", e.target.value)}
              className="ghost-input mt-2"
              data-testid="editor-publish-date-input"
            />
            <p className="mt-1 font-mono text-[10px] text-[#66726d]">
              Leave blank to use the current moment.
            </p>
          </div>
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.featured}
            onChange={(e) => set("featured", e.target.checked)}
            data-testid="editor-featured-checkbox"
          />
          <span className="font-mono text-[10px] uppercase tracking-widest">Feature on homepage</span>
        </label>

        <div className="flex gap-3 pt-4">
          <button type="submit" className="btn-primary" disabled={saving} data-testid="editor-save-button">
            {buttonLabel}
          </button>
          <Link to="/admin" className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
